import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../../components/layout/AppLayout';
import { useCourseApi } from '../../../../api/courses/courses';
import { TLesson } from '../../../../models/courses/lesson';
import PuzzleLessonAdmin from '../../../../components/PuzzleLesson/admin/PuzzleLessonAdmin';
import PuzzleLessonTeacher from '../../../../components/PuzzleLesson/teacher/PuzzleLessonTeacher';
import { TPuzzleLessonConfig, createEmptyPuzzleLessonConfig } from '../../../../models/courses/puzzleLesson';

const LessonPuzzle: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getLessonDetailsById, saveLessonDetails } = useCourseApi();

  const stateLesson: TLesson | undefined = location.state?.lesson;
  const teacherMode: boolean = location.state?.teacherMode || false;
  const epic = location.state?.epic;

  const [editingLesson, setEditingLesson] = useState<TLesson | null>(stateLesson ?? null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importedStatement, setImportedStatement] = useState<string>('');

  const lessonIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const lessonId = params.get('lessonId');
    return lessonId ? parseInt(lessonId, 10) : undefined;
  }, [location.search]);

  useEffect(() => {
    const lessonId = lessonIdFromUrl ?? stateLesson?.id;
    if (!lessonId) {
      message.warning(t('puzzleLesson.page.messages.selectLesson') || 'Choisissez une leçon');
      navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
      return;
    }
    setLoading(true);
    getLessonDetailsById(lessonId)
      .then((response) => {
        const lessonData = response.data;
        const existingConfig = lessonData.config?.config as TPuzzleLessonConfig | undefined;
        lessonData.config = {
          id: lessonData.config?.id ?? 0,
          lesson_id: lessonData.id,
          config: existingConfig?.components ? existingConfig : createEmptyPuzzleLessonConfig(),
        };
        setEditingLesson(lessonData);
        setImportedStatement(lessonData.statement || '');
      })
      .catch((error) => {
        message.error(error.response?.data ?? error.message);
        navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line 
  }, []);

  const currentConfig: TPuzzleLessonConfig = useMemo(() => {
    const rawConfig = editingLesson?.config?.config as TPuzzleLessonConfig | undefined;
    if (rawConfig?.components) {
      return { ...createEmptyPuzzleLessonConfig(), ...rawConfig };
    }
    return createEmptyPuzzleLessonConfig();
  }, [editingLesson]);

  const handleConfigChange = (config: TPuzzleLessonConfig) => {
    setEditingLesson((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        config: {
          id: prev.config?.id ?? 0,
          lesson_id: prev.id,
          config,
        },
      };
    });
  };

  const handleSave = async () => {
    if (!editingLesson) return;
    setSaving(true);
    try {
      const payload: TLesson = {
        ...editingLesson,
        statement: importedStatement || editingLesson.statement || '',
        config: {
          id: editingLesson.config?.id ?? 0,
          lesson_id: editingLesson.id,
          config: {
            ...(editingLesson.config?.config as TPuzzleLessonConfig),
            ...currentConfig,
            statement: importedStatement || editingLesson.statement || '',
          },
        },
      };
      await saveLessonDetails(payload);
      message.success(t('puzzleLesson.page.messages.saveSuccess') || 'Configuration sauvegardée');
    } catch (error: any) {
      message.error(error.response?.data ?? error.message ?? error);
    } finally {
      setSaving(false);
    }
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <AppLayout
      title={editingLesson?.name || t('puzzleLesson.page.title') || 'Lesson Puzzle'}
      onBackNavigation={handleBackNavigation}
      showBackButton
      loading={loading}
    >
      {editingLesson && (
        teacherMode ? (
          <PuzzleLessonTeacher
            config={currentConfig}
            statement={editingLesson.statement || currentConfig.statement}
          />
        ) : (
          <PuzzleLessonAdmin
            config={currentConfig}
            onConfigChange={handleConfigChange}
            onSave={handleSave}
            saving={saving}
            initialStatement={importedStatement}
            onStatementChange={setImportedStatement}
          />
        )
      )}
    </AppLayout>
  );
};

export default LessonPuzzle;
